import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks'
import { ConfigurationSteps } from '@forwardflow/modules/onboarding/steps/campaign-configuration/ConfigurationSteps'
import CampaignService from '@forwardflow/services/CampaignService'
import { useCampaignStore } from '@forwardflow/store'
import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal'

import { message } from 'antd'
import { Modal, Overlay } from '@mantine/core'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { TextInput } from '@forwardflow/ui-v2'

export const NewCampaignModal = () => {
   const { title: leadSource } = useParams()
   const { data: activeLeadSourceData, mutate: mutateActiveLeadSource } =
      useGetActiveLeadSource(leadSource as string)
   const { mutate: mutateActiveLeadSourceDetails } =
      useGetActiveLeadSourceDetails(leadSource as string)
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()

   const {
      campaignConfiguration,
      resetCampaignConfiguration,
      setCampaignConfiguration,
   } = useCampaignStore()

   const {
      isAdvanceTargetingCampaignModalOpen,
      closeAdvanceTargetingCampaignModal,
   } = useAdvanceTargetingCampaignModalStore()
   const [isLoading, setIsLoading] = useState(false)
   const [nameError, setNameError] = useState<string>('')

   const isEditMode = false

   const handleLaunchCampaign = async () => {
      // Validate campaign name
      if (!campaignConfiguration?.campaign_name?.trim()) {
         setNameError('Campaign name is required')
         return
      }
      setNameError('')

      setIsLoading(true)
      try {
         let responseActiveLeadSource: any
         let responseUpdatedLeadSource: any
         if (isEditMode && activeLeadSourceData?.id) {
            responseActiveLeadSource =
               await CampaignService.editCustomActiveLeadSource(
                  activeLeadSourceData?.id,
                  campaignConfiguration as any
               )
            responseUpdatedLeadSource =
               await CampaignService.updateCampaignName(
                  activeLeadSourceData?.id,
                  campaignConfiguration?.campaign_name
               )
         } else {
            responseActiveLeadSource =
               await CampaignService.createCustomActiveLeadSource(
                  campaignConfiguration?.campaign_name as string,
                  campaignConfiguration?.campaign_filters as any,
                  campaignConfiguration?.campaign_type as
                     | 'new-lead'
                     | 'advanced',
                  {
                     bufferTime:
                        campaignConfiguration?.first_to_contact?.buffer_time ||
                        0,
                  },
                  true
               )
         }
         if (responseUpdatedLeadSource) {
            message.success('Campaign updated successfully')

            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.set(
               'campaignName',
               responseUpdatedLeadSource?.campaignName
            )
            navigate(
               `${window.location.pathname}?${newSearchParams.toString()}`,
               { replace: true }
            )
         }
         if (responseActiveLeadSource && responseActiveLeadSource.id) {
            if (isEditMode) {
               message.success('Campaign updated successfully')
            } else {
               navigate(
                  `/campaign/${encodeURIComponent(
                     responseActiveLeadSource.id
                  )}/phone-number?campaignName=${
                     campaignConfiguration?.campaign_name
                  }`
               )
            }
         }
      } catch (e) {
         message.error(
            isEditMode
               ? 'Failed to update campaign'
               : 'Failed to create campaign'
         )
      } finally {
         closeAdvanceTargetingCampaignModal()
         setIsLoading(false)
         mutateActiveLeadSource()
         mutateActiveLeadSourceDetails()
         resetCampaignConfiguration()
      }
   }

   return (
      <Modal
         centered
         title=""
         size="xl"
         radius="lg"
         classNames={{
            body: 'p-6',
         }}
         opened={isAdvanceTargetingCampaignModalOpen}
         onClose={closeAdvanceTargetingCampaignModal}
      >
         {isLoading && (
            <Overlay
               blur={3}
               center
               fixed={false}
               className="z-50"
               styles={{
                  root: {
                     position: 'absolute',
                  },
               }}
            >
               <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                  <p className="text-white font-medium">
                     {isEditMode
                        ? 'Updating campaign...'
                        : 'Creating campaign...'}
                  </p>
               </div>
            </Overlay>
         )}
         <div className="max-w-[850px] mx-auto space-y-6">
            <TextInput
               label="Campaign Name"
               name="campaign_name"
               placeholder="Enter campaign name"
               value={campaignConfiguration?.campaign_name}
               onChange={(e) => {
                  setCampaignConfiguration({
                     ...campaignConfiguration,
                     campaign_name: e.target.value,
                  })
                  if (nameError) {
                     setNameError('')
                  }
               }}
               error={nameError}
            />
            <ConfigurationSteps onNextStep={handleLaunchCampaign} />
         </div>
      </Modal>
   )
}
